/**
 * The dispute queue, and the one decision that ends a dispute.
 *
 * **Opening a dispute holds the pandit's payout — except when the pandit raised it.** They are
 * contesting a ruling about themselves, usually a no-show finding, and freezing their own money
 * for doing so would make raising one cost them. The screen says which of the two happened on
 * every dispute, because "held" and "not held" look identical otherwise and only one of them
 * means somebody is waiting to be paid.
 *
 * **There is no `resolved` in the status dropdown.** A dispute is resolved by recording a
 * decision with its reasoning, and a status write that could reach `resolved` would let one be
 * closed with nothing behind it — the decision is what both parties are owed.
 *
 * **The four-eyes ceiling is the server's.** Above `settlement.four_eyes_threshold_paise` a
 * second admin has to agree and it may not be the person deciding; this screen offers the field
 * and shows the refusal verbatim rather than keeping its own copy of the number, which would be
 * wrong and confident the first time ops moved it.
 */
import { useState } from 'react';
import { DisputeEvidenceLink } from './SupportDesk';
import { HowDisputesResolve } from './DisputesHowTo';
import { Button, StatusChip, type Tone } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, FilterBar, Money, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { formatInr, parsePaise } from '../lib/money';
import {
  useAssignDispute,
  useDispute,
  useDisputeBundle,
  useDisputes,
  useResolveDispute,
  useSetDisputeStatus,
} from '../lib/api/hooks/support';
import { formatDateTime } from '../lib/datetime';

const PAGE_SIZE = 50;
const ALL = 'every state';

/** The server's vocabulary — ops read the same words in the logs and in the audit trail. */
const STATES = [ALL, 'open', 'evidence_pending', 'under_review', 'resolved', 'withdrawn'];

/**
 * The states a status write may reach. `resolved` and `withdrawn` are both absent and for
 * different reasons: resolving needs a decision, and withdrawing belongs to the person who
 * raised it, not to us.
 */
const MOVES = ['evidence_pending', 'under_review', 'open'];
/** The states in which evidence can still change a decision (`disputes.LIVE_STATUSES`). */
const LIVE = ['open', 'evidence_pending', 'under_review'];

const OUTCOMES = [
  'full_refund',
  'partial_refund',
  'goodwill_credit',
  'payout_forfeit_full',
  'payout_forfeit_partial',
  'payout_release',
  'provider_quality_action',
  'no_action',
];

/** The outcomes that move money. The rest cost nobody anything, so no ceiling applies. */
const MONEY_OUTCOMES = new Set([
  'full_refund',
  'partial_refund',
  'goodwill_credit',
  'payout_forfeit_full',
  'payout_forfeit_partial',
]);

/**
 * Which amounts each outcome is allowed to carry — §5.5's matrix, as the shape of the form.
 *
 * The server enforces this and refuses anything else. The form mirrors it so an operator is not
 * offered a field the decision cannot use and then told no after typing a figure into it; a
 * `no_action` with a refund box is an invitation to record two decisions under one name.
 */
/** The booking a dispute is about, as an operator reads it aloud: its BMP code. The bare id is
 *  an internal integer no caller or pandit has ever seen (live 2026-10-30 showed "8", "19"). */
export function disputeMovesFrom(status: string): string[] {
  return MOVES.filter((m) => m !== status);
}

/** A deadline, and whether it has gone. Live 2026-10-30: four disputes sat days past their
 *  decision date with nothing on screen saying so. */
export function dueLabel(iso: string, now: number = Date.now()): string {
  return `${formatDateTime(iso)}${Date.parse(iso) < now ? ' · overdue' : ''}`;
}

export function disputeBooking(d: { booking_id: number; booking_public_code?: string | null }): string {
  return d.booking_public_code ?? `booking #${d.booking_id}`;
}

export function amountsFor(outcome: string): { refund: boolean; forfeit: boolean } {
  if (outcome === 'no_action' || outcome === 'payout_release') return { refund: false, forfeit: false };
  if (outcome === 'provider_quality_action') return { refund: false, forfeit: false };
  /* Platform-funded, always: a goodwill gesture taken out of the performer's fee is not one. */
  if (outcome === 'goodwill_credit') return { refund: true, forfeit: false };
  return { refund: true, forfeit: true };
}

const TABS = ['The case', 'The bundle'] as const;

/**
 * Minutes, hours or days — whichever an operator would say out loud.
 *
 * A check-in distance of 214 m is read as-is; a *time* of 4320 minutes is not, and a reviewer
 * doing that arithmetic under time pressure is a reviewer who will occasionally get it wrong.
 */
export function metres(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'no location recorded';
  return value >= 1000 ? `${(value / 1000).toFixed(1)} km from the venue` : `${value} m from the venue`;
}

/**
 * A rate as a percentage, or an honest blank.
 *
 * `0` and "never computed" are different facts and must not render alike: one says the pandit
 * fails every time, the other says nobody has measured.
 */
export function rate(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return 'not computed';
  return `${(Number(value) * 100).toFixed(0)}%`;
}

/**
 * One OTP, as a sentence.
 *
 * Extracted so all four shapes can be asserted, including the odd one: `verified_at` set with
 * no `verified_by`. The completion service always writes both together, but nothing in the
 * schema pairs them — and a verification with no verifier is exactly the anomaly a reviewer
 * deciding a no-show needs to see stated, not rendered as "by the null".
 */
export function otpLine(o: {
  otp_type: string;
  verified_at?: string | null;
  verified_by?: string | null;
  is_locked: boolean;
  attempt_count: number;
}): string {
  const locked = o.is_locked ? ` · locked after ${o.attempt_count} attempts` : '';
  if (o.verified_at === null || o.verified_at === undefined) {
    return `${o.otp_type} · never verified${locked}`;
  }
  const by =
    o.verified_by === null || o.verified_by === undefined
      ? ', verifier not recorded'
      : ` by the ${o.verified_by}`;
  return `${o.otp_type} · verified ${formatDateTime(o.verified_at)}${by}${locked}`;
}

export function statusTone(status: string): Tone {
  if (status === 'resolved') return 'success';
  if (status === 'withdrawn') return 'neutral';
  if (status === 'under_review') return 'warn';
  return 'error';
}

/**
 * Whether this dispute is holding the pandit's money.
 *
 * Derived from who raised it rather than from a flag, because that is exactly how the backend
 * decides: `open_dispute` pauses the payable for everybody except the pandit.
 */
export function holdsThePayout(raisedBy: string): boolean {
  return raisedBy !== 'pandit';
}

export function Disputes() {
  const { scope, adminId, toast } = useConsole();

  const [state, setState] = useState(ALL);
  const [page] = useState(1);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const [moveTo, setMoveTo] = useState('');
  const [outcome, setOutcome] = useState(OUTCOMES[0]!);
  const [refund, setRefund] = useState('');
  const [forfeit, setForfeit] = useState('');
  const [second, setSecond] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [tab, setTab] = useState<(typeof TABS)[number]>('The case');
  const [howTo, setHowTo] = useState(false);

  const list = useDisputes({
    page,
    page_size: PAGE_SIZE,
    ...(state === ALL ? {} : { dispute_status: state }),
  });
  const detail = useDispute(openCode);
  const moves = disputeMovesFrom(detail.data?.dispute.status ?? '');
  const moveTarget = moves.includes(moveTo) ? moveTo : moves[0]!;
  /* Only when the tab is open. The bundle is a wide read across eight tables, and fetching it
     because a detail page rendered would run it for every dispute somebody merely glanced at. */
  const bundle = useDisputeBundle(openCode, tab === 'The bundle');
  const move = useSetDisputeStatus();
  const take = useAssignDispute();
  const decide = useResolveDispute();

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });

  /* An unparseable amount is treated as no amount at all, never as zero — zero is a decision. */
  const refundPaise = refund.trim() ? parsePaise(refund) : null;
  const forfeitPaise = forfeit.trim() ? parsePaise(forfeit) : null;
  const secondId = second.trim() === '' ? null : Number(second.trim());
  const movesMoney = MONEY_OUTCOMES.has(outcome);
  const amounts = amountsFor(outcome);
  /* What is actually sent. A figure typed under `partial_refund` and left behind when the
     operator switched to `no_action` is not part of that decision — the field is gone from the
     form, and sending it anyway would earn a refusal the operator could not see the cause of. */
  const sendRefund = amounts.refund ? refundPaise : null;
  const sendForfeit = amounts.forfeit ? forfeitPaise : null;

  if (openCode !== null) {
    const dispute = detail.data?.dispute;
    const resolution = detail.data?.resolution ?? null;
    const held = dispute !== undefined && holdsThePayout(dispute.raised_by);

    return (
      <DetailPage
        title={openCode}
        tabs={[...TABS]}
        activeTab={tab}
        onTab={(next) => setTab(next as (typeof TABS)[number])}
        onClose={() => {
          setOpenCode(null);
          setRefund('');
          setForfeit('');
          setSecond('');
        }}
        actions={
          detail.isSuccess && dispute?.assigned_admin_id !== adminId ? (
            <ScopeAction
              scope={scope}
              need="support"
              size="sm"
              variant="ghost"
              onClick={() =>
                take.mutate(
                  { code: openCode, adminId },
                  {
                    onSuccess: () => toast({ tone: 'success', message: 'Yours' }),
                    onError: fail,
                  },
                )
              }
            >
              Take this dispute
            </ScopeAction>
          ) : null
        }
      >
        {tab === 'The bundle' && (
          <>
            {bundle.isPending && <LoadingState message="Assembling the bundle…" />}
            {bundle.isError && (
              <ErrorState
                title="Could not assemble the bundle"
                cause={errorMessage(bundle.error)}
              />
            )}
            {bundle.isSuccess && (
              <>
                <Section
                  title="What was booked"
                  note={`${bundle.data.puja_name ?? 'puja'}${
                    bundle.data.package_name === null || bundle.data.package_name === undefined
                      ? ''
                      : ` · ${bundle.data.package_name}`
                  } · ${bundle.data.city_name ?? 'city not recorded'}`}
                >
                  <KV
                    rows={[
                      ['Booking', bundle.data.booking_public_code],
                      ['Booking state', bundle.data.booking_status.replace(/_/g, ' ')],
                      ['Scheduled', formatDateTime(bundle.data.scheduled_start_at)],
                      ...bundle.data.line_items.map(
                        (i) =>
                          [i.label, <Money key={i.label} paise={i.amount_paise} showPaise />] as const,
                      ),
                    ]}
                  />
                </Section>

                <Section
                  title="Was the pandit there"
                  note="Distances are computed server-side and truncated, never rounded — rounding can move a point towards the venue"
                >
                  {bundle.data.check_ins.length === 0 ? (
                    <EmptyState inline title="No check-in was recorded" body="Which is itself a fact about this booking." />
                  ) : (
                    bundle.data.check_ins.map((c, i) => (
                      <div key={`${c.event_type}-${i}`} style={{ fontSize: 'var(--fs-admin-ui)' }}>
                        {c.event_type.replace(/_/g, ' ')} · {formatDateTime(c.recorded_at)} · {metres(c.distance_from_venue_m)}
                      </div>
                    ))
                  )}
                </Section>

                <Section
                  title="Did both sides confirm it"
                  note="Both codes go to the devotee: the start code proves the pandit was there to be told it, the completion code proves the devotee agreed it happened"
                >
                  {bundle.data.otps.length === 0 ? (
                    <EmptyState inline title="No code was ever issued" body="Nothing was verified either way." />
                  ) : (
                    bundle.data.otps.map((o, i) => (
                      <div key={`${o.otp_type}-${i}`} style={{ fontSize: 'var(--fs-admin-ui)' }}>
                        {otpLine(o)}
                      </div>
                    ))
                  )}
                </Section>

                <Section
                  title="Completion evidence"
                  note="Opening it is a separate, logged act — this page never mints a link, because a look nobody took should not appear in a devotee's access trail"
                >
                  <KV
                    rows={[
                      ['Objects', String(bundle.data.evidence.object_count)],
                      [
                        'Kinds',
                        bundle.data.evidence.kinds.length === 0
                          ? 'none'
                          : bundle.data.evidence.kinds.join(', ').replace(/_/g, ' '),
                      ],
                      [
                        'Your access',
                        bundle.data.evidence.caller_has_live_grant
                          ? 'you hold a live dispute-review grant'
                          : 'you would need a grant first',
                      ],
                    ]}
                  />
                </Section>

                <Section title="Where the money is">
                  <KV
                    rows={[
                      ['Devotee paid', <Money key="t" paise={bundle.data.money.booking_total_paise} showPaise />],
                      ['Payment', bundle.data.money.payment_status ?? 'nothing captured'],
                      ['Payable', bundle.data.money.payable_status ?? 'none yet'],
                      [
                        'Held because',
                        bundle.data.money.open_pause_kinds.length === 0
                          ? bundle.data.money.payable_hold_reason ?? 'nothing is holding it'
                          : bundle.data.money.open_pause_kinds.join(', '),
                      ],
                      ...bundle.data.money.refunds.map(
                        (r) =>
                          [
                            `Refund ${r.label}`,
                            <span key={r.label}>
                              {r.status} · <Money paise={r.amount_paise} showPaise />
                            </span>,
                          ] as const,
                      ),
                      ...bundle.data.money.adjustments.map(
                        (a, i) =>
                          [
                            `Adjustment ${a.label}`,
                            <span key={`${a.label}-${i}`}>
                              {a.status} · <Money paise={a.amount_paise} showPaise />
                            </span>,
                          ] as const,
                      ),
                    ]}
                  />
                </Section>

                <Section
                  title="Both sides' record"
                  note="Counts with denominators, never labels — the words are yours to choose"
                >
                  <KV
                    rows={[
                      ['Pandit open flags', String(bundle.data.history.pandit_open_flags)],
                      [
                        'Pandit rating',
                        bundle.data.history.pandit_rating_count === null ||
                        bundle.data.history.pandit_rating_count === undefined ||
                        bundle.data.history.pandit_rating_count === 0
                          ? 'never rated'
                          : `${bundle.data.history.pandit_rating_avg} over ${bundle.data.history.pandit_rating_count}`,
                      ],
                      ['Pandit completion rate', rate(bundle.data.history.pandit_completion_rate)],
                      ['Pandit on-time rate', rate(bundle.data.history.pandit_on_time_rate)],
                      [
                        'Devotee disputes',
                        `${bundle.data.history.devotee_disputes_raised} raised across ${bundle.data.history.devotee_bookings} booking${bundle.data.history.devotee_bookings === 1 ? '' : 's'}`,
                      ],
                    ]}
                  />
                </Section>

                {bundle.data.thread.length > 0 && (
                  <Section
                    title="The conversation"
                    note="Internal notes are shown to you here — often the most load-bearing line in the thread"
                  >
                    {bundle.data.thread.map((m) => (
                      <div key={m.id} style={{ marginBottom: 'var(--sp-sm)' }}>
                        <div style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink2)' }}>
                          {m.author_type}
                          {m.is_internal_note ? ' · internal note' : ''}
                        </div>
                        <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', whiteSpace: 'pre-wrap' }}>
                          {m.body}
                        </p>
                      </div>
                    ))}
                  </Section>
                )}
              </>
            )}
          </>
        )}

        {tab === 'The case' && detail.isPending && <LoadingState message="Reading the dispute…" />}
        {tab === 'The case' && detail.isError && (
          <ErrorState title="Could not read the dispute" cause={errorMessage(detail.error)} />
        )}

        {tab === 'The case' && detail.isSuccess && dispute !== undefined && (
          <>
            <Section
              title={`${dispute.dispute_type.replace(/_/g, ' ')} · raised by the ${dispute.raised_by}`}
              note={
                held
                  ? 'The pandit’s payout is held until this is decided.'
                  : 'The pandit raised this, so their payout is NOT held — contesting a ruling about yourself should not cost you.'
              }
            >
              <KV
                rows={[
                  ['Booking', disputeBooking(dispute)],
                  ['State', dispute.status.replace(/_/g, ' ')],
                  [
                    'Disputed amount',
                    dispute.disputed_amount_paise === null ||
                    dispute.disputed_amount_paise === undefined ? (
                      'not stated'
                    ) : (
                      <Money key="amt" paise={dispute.disputed_amount_paise} showPaise />
                    ),
                  ],
                  ['Evidence due', dispute.evidence_due_at ? formatDateTime(dispute.evidence_due_at) : 'no window set'],
                  ['Decision due', dueLabel(dispute.resolution_due_at)],
                  [
                    'Held by',
                    dispute.assigned_admin_id === null || dispute.assigned_admin_id === undefined
                      ? 'nobody yet'
                      : `admin ${dispute.assigned_admin_id}`,
                  ],
                ]}
              />
              <p style={{ fontSize: 'var(--fs-admin-ui)', whiteSpace: 'pre-wrap' }}>
                {dispute.statement}
              </p>
            </Section>

            <Section
              title="Evidence"
              note="Completion photos stay private — they are never published and never used in marketing"
            >
              {detail.data.evidence.length === 0 ? (
                <EmptyState
                  inline
                  title="Nothing filed yet"
                  body="Either side can file until the evidence window closes."
                />
              ) : (
                detail.data.evidence.map((e) => (
                  <div key={e.id} style={{ marginBottom: 'var(--sp-sm)' }}>
                    <div style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink2)' }}>
                      {e.evidence_kind.replace(/_/g, ' ')} · filed by the {e.submitted_by}
                    </div>
                    {e.external_note !== null && e.external_note !== undefined && (
                      <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)' }}>{e.external_note}</p>
                    )}
                  </div>
                ))
              )}
              {/* Only while it can still change a decision — the server refuses it afterwards. */}
              {LIVE.includes(dispute.status) && <DisputeEvidenceLink code={openCode} />}
            </Section>

            {resolution !== null ? (
              <Section title="The decision" note="Both parties are shown the reasoning">
                <KV
                  rows={[
                    ['Outcome', resolution.outcome.replace(/_/g, ' ')],
                    [
                      'Refunded',
                      resolution.refund_amount_paise === null ||
                      resolution.refund_amount_paise === undefined ? (
                        'nothing'
                      ) : (
                        <Money key="r" paise={resolution.refund_amount_paise} showPaise />
                      ),
                    ],
                    [
                      'Forfeited from the payout',
                      resolution.payout_forfeit_paise === null ||
                      resolution.payout_forfeit_paise === undefined ? (
                        'nothing'
                      ) : (
                        <Money key="f" paise={resolution.payout_forfeit_paise} showPaise />
                      ),
                    ],
                    ['Decided', formatDateTime(resolution.decided_at)],
                  ]}
                />
                <p style={{ fontSize: 'var(--fs-admin-ui)', whiteSpace: 'pre-wrap' }}>
                  {resolution.rationale}
                </p>
              </Section>
            ) : (
              <>
                <Section
                  title="Move it"
                  note="Resolved is not on this list — a dispute is resolved by recording the decision below, never by setting a status"
                >
                  <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end' }}>
                    <Select label="To" value={moveTarget} options={moves} onChange={setMoveTo} />
                    <ScopeAction
                      scope={scope}
                      need="support"
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        move.mutate(
                          { code: openCode, status: moveTarget },
                          {
                            onSuccess: () => toast({ tone: 'success', message: 'Moved' }),
                            onError: fail,
                          },
                        )
                      }
                    >
                      Move
                    </ScopeAction>
                  </div>
                </Section>

                <Section
                  title="Decide it"
                  note="This ends the payout hold, whichever way it goes"
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                      gap: 'var(--sp-md)',
                    }}
                  >
                    <Select
                      label="Outcome"
                      value={outcome}
                      options={OUTCOMES}
                      onChange={setOutcome}
                    />
                    {amounts.refund && (
                      <Input
                        prefix="₹"
                        inputMode="decimal"
                        label="Refund to the devotee"
                        value={refund}
                        onChange={setRefund}
                        error={
                          refund.trim() && refundPaise === null ? 'Not a rupee amount' : undefined
                        }
                      />
                    )}
                    {amounts.forfeit && (
                      <Input
                        prefix="₹"
                        inputMode="decimal"
                        label="Forfeit from the payout"
                        value={forfeit}
                        onChange={setForfeit}
                        error={
                          forfeit.trim() && forfeitPaise === null ? 'Not a rupee amount' : undefined
                        }
                      />
                    )}
                    <Input
                      inputMode="numeric"
                      label="Seconded by (admin id)"
                      value={second}
                      onChange={setSecond}
                      helper="Needed above the four-eyes ceiling"
                    />
                  </div>
                  {movesMoney && (
                    <p
                      style={{
                        fontSize: 'var(--fs-admin-colhead)',
                        color: 'var(--c-ink2)',
                      }}
                    >
                      Above <span className="mono-num">settlement.four_eyes_threshold_paise</span> a
                      second support or finance admin has to agree, and it cannot be you — one
                      person with two clicks is still one person. The server checks the ceiling;
                      if it refuses, its answer is shown here.
                    </p>
                  )}
                  <ScopeAction
                    scope={scope}
                    need={['support', 'finance']}
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() => setDeciding(true)}
                  >
                    Record the decision
                  </ScopeAction>
                </Section>
              </>
            )}
          </>
        )}

        {deciding && (
          <ConfirmAction
            open
            title="Record this decision?"
            confirmLabel="Record it"
            phrase="RESOLVE"
            tone="error"
            reason="Rationale — both parties are shown this"
            consequences={[
              `The dispute closes as ${outcome.replace(/_/g, ' ')} and cannot be reopened as this one.`,
              held
                ? 'The dispute hold on the pandit’s payout ends — released, or clawed back if you forfeit.'
                : 'No dispute hold was placed, so nothing about the payout changes here.',
              sendRefund === null
                ? 'No refund is issued.'
                : `Refunds ${formatInr(sendRefund, { paise: true })} to the devotee.`,
              sendForfeit === null
                ? 'Nothing is forfeited from the payout.'
                : `Forfeits ${formatInr(sendForfeit, { paise: true })} from the pandit’s payout.`,
              movesMoney && secondId === null
                ? 'Above the four-eyes ceiling this will be refused until a second admin seconds it.'
                : 'Recorded against your name in the audit log.',
            ]}
            onCancel={() => setDeciding(false)}
            onConfirm={(rationale) =>
              decide.mutate(
                {
                  code: openCode,
                  outcome,
                  rationale,
                  ...(sendRefund === null ? {} : { refundAmountPaise: sendRefund }),
                  ...(sendForfeit === null ? {} : { payoutForfeitPaise: sendForfeit }),
                  ...(secondId === null ? {} : { secondApproverAdminId: secondId }),
                },
                {
                  onSuccess: () => {
                    setDeciding(false);
                    toast({ tone: 'success', message: 'Decision recorded' });
                  },
                  onError: (error) => {
                    setDeciding(false);
                    fail(error);
                  },
                },
              )
            }
          />
        )}
      </DetailPage>
    );
  }

  return (
    <>
      <FilterBar
        extra={<Select label="State" value={state} options={STATES} onChange={setState} />}
        right={
          <>
            <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
              Opening a dispute holds the pandit’s payout · one the pandit raised does not
            </span>
            <Button size="sm" variant="ghost" onClick={() => setHowTo(true)}>
              How disputes resolve
            </Button>
          </>
        }
      />
      {howTo && <HowDisputesResolve onClose={() => setHowTo(false)} />}

      {list.isPending && <LoadingState message="Reading the queue…" />}
      {list.isError && (
        <ErrorState title="Could not read the queue" cause={errorMessage(list.error)} />
      )}
      {list.isSuccess && list.data.items.length === 0 && (
        <EmptyState
          title="Nothing contested"
          body="Disputes arrive from the issue window, from a pandit contesting a ruling, and from support escalating a ticket."
        />
      )}
      {list.isSuccess && list.data.items.length > 0 && (
        <DataTable
          density="compact"
          onRowClick={(row) => setOpenCode(String(row.id))}
          columns={[
            { key: 'code', label: 'Dispute', mono: true },
            { key: 'booking', label: 'Booking', mono: true },
            { key: 'kind', label: 'About' },
            { key: 'raised', label: 'Raised by' },
            { key: 'payout', label: 'Payout' },
            { key: 'amount', label: 'Disputed', align: 'right', mono: true },
            { key: 'status', label: 'State' },
          ]}
          rows={list.data.items.map((d) => ({
            id: d.public_code,
            code: d.public_code,
            booking: disputeBooking(d),
            kind: d.dispute_type.replace(/_/g, ' '),
            raised: d.raised_by,
            payout: holdsThePayout(d.raised_by) ? 'held' : 'not held',
            amount:
              d.disputed_amount_paise === null || d.disputed_amount_paise === undefined ? (
                '—'
              ) : (
                <Money paise={d.disputed_amount_paise} showPaise />
              ),
            status: <StatusChip tone={statusTone(d.status)} label={d.status.replace(/_/g, ' ')} />,
          }))}
          footer={`${list.data.total} dispute${list.data.total === 1 ? '' : 's'}`}
        />
      )}
    </>
  );
}
